import React, { useState, useEffect } from 'react';
import { getClientWallet, getWalletTransactions, topupWallet, refundToWallet, payFromWallet, getInvoices } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Wallet, 
  Plus, 
  ArrowUp, 
  ArrowDown, 
  RefreshCw, 
  AlertTriangle,
  CreditCard,
  Receipt,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';

const transactionTypeColors = {
  'TopUp': 'bg-green-100 text-green-700 ring-1 ring-green-300',
  'Payment': 'bg-blue-100 text-blue-700 ring-1 ring-blue-300',
  'Refund': 'bg-amber-100 text-amber-700 ring-1 ring-amber-300'
};

const transactionTypeIcons = {
  'TopUp': <ArrowDown className="w-4 h-4 text-green-600" />,
  'Payment': <ArrowUp className="w-4 h-4 text-blue-600" />,
  'Refund': <RefreshCw className="w-4 h-4 text-amber-600" />
};

export default function WalletSection({ clientId, clientName }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [saving, setSaving] = useState(false);

  const [topupData, setTopupData] = useState({
    amount: '',
    payment_method: 'Cash',
    reference_number: '',
    notes: ''
  });

  const [refundData, setRefundData] = useState({
    amount: '',
    reason: '',
    notes: ''
  });

  const [payData, setPayData] = useState({
    invoice_id: '',
    amount: '',
    notes: ''
  });

  useEffect(() => {
    if (clientId) {
      loadWalletData();
      loadUnpaidInvoices();
    }
  }, [clientId]);

  const loadWalletData = async () => {
    try {
      const [walletRes, txRes] = await Promise.all([
        getClientWallet(clientId),
        getWalletTransactions(clientId, { limit: 20 })
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data.items);
    } catch (error) {
      console.error('Failed to load wallet', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnpaidInvoices = async () => {
    try {
      const res = await getInvoices({ client_id: clientId, limit: 100 });
      setUnpaidInvoices(res.data.items.filter(inv => inv.balance_due > 0));
    } catch (error) {
      console.error('Failed to load invoices', error);
    }
  };

  const handleTopup = async () => {
    if (!topupData.amount || parseFloat(topupData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const res = await topupWallet(clientId, {
        amount: parseFloat(topupData.amount),
        payment_method: topupData.payment_method,
        reference_number: topupData.reference_number || undefined,
        notes: topupData.notes || undefined
      });
      toast.success(res.data.message);
      setTopupDialogOpen(false);
      setTopupData({ amount: '', payment_method: 'Cash', reference_number: '', notes: '' });
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to top up wallet');
    } finally {
      setSaving(false);
    }
  };

  const handleRefund = async () => {
    if (!refundData.amount || parseFloat(refundData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!refundData.reason) {
      toast.error('Please enter a reason');
      return;
    }

    setSaving(true);
    try {
      const res = await refundToWallet(clientId, {
        amount: parseFloat(refundData.amount),
        reason: refundData.reason,
        notes: refundData.notes || undefined
      });
      toast.success(res.data.message);
      setRefundDialogOpen(false);
      setRefundData({ amount: '', reason: '', notes: '' });
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refund');
    } finally {
      setSaving(false);
    }
  };

  const handlePayFromWallet = async () => {
    if (!payData.amount || parseFloat(payData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const res = await payFromWallet(clientId, {
        invoice_id: payData.invoice_id || undefined,
        amount: parseFloat(payData.amount),
        notes: payData.notes || undefined
      });
      toast.success(res.data.message);
      if (res.data.low_balance_alert) {
        toast.warning('Wallet balance is now below threshold');
      }
      setPayDialogOpen(false);
      setPayData({ invoice_id: '', amount: '', notes: '' });
      loadWalletData();
      loadUnpaidInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading wallet...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Wallet Balance Card */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Wallet Balance</p>
                <p className="text-3xl font-bold text-primary" data-testid="wallet-balance">
                  {formatCurrency(wallet?.balance || 0)}
                </p>
                {wallet?.low_balance_alert && (
                  <div className="flex items-center gap-1 mt-1 text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Low balance alert</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {/* Top Up Dialog */}
              <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="wallet-topup-btn">
                    <Plus className="w-4 h-4" /> Top Up
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Top Up Wallet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Amount (AED) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={topupData.amount}
                        onChange={(e) => setTopupData(p => ({ ...p, amount: e.target.value }))}
                        placeholder="Enter amount"
                        data-testid="topup-amount"
                      />
                    </div>
                    <div>
                      <Label>Payment Method *</Label>
                      <Select value={topupData.payment_method} onValueChange={(v) => setTopupData(p => ({ ...p, payment_method: v }))}>
                        <SelectTrigger data-testid="topup-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Card">Card</SelectItem>
                          <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                          <SelectItem value="Cheque">Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Reference Number</Label>
                      <Input
                        value={topupData.reference_number}
                        onChange={(e) => setTopupData(p => ({ ...p, reference_number: e.target.value }))}
                        placeholder="Transaction/Receipt #"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={topupData.notes}
                        onChange={(e) => setTopupData(p => ({ ...p, notes: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <Button className="w-full" onClick={handleTopup} disabled={saving} data-testid="confirm-topup-btn">
                      {saving ? 'Processing...' : 'Confirm Top Up'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Pay from Wallet Dialog */}
              <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="wallet-pay-btn">
                    <CreditCard className="w-4 h-4" /> Pay
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Pay from Wallet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Available Balance</p>
                      <p className="text-xl font-bold text-primary">{formatCurrency(wallet?.balance || 0)}</p>
                    </div>
                    
                    {unpaidInvoices.length > 0 && (
                      <div>
                        <Label>Apply to Invoice (Optional)</Label>
                        <Select value={payData.invoice_id} onValueChange={(v) => {
                          const inv = unpaidInvoices.find(i => i.id === v);
                          setPayData(p => ({ 
                            ...p, 
                            invoice_id: v,
                            amount: inv ? Math.min(inv.balance_due, wallet?.balance || 0).toString() : p.amount
                          }));
                        }}>
                          <SelectTrigger data-testid="pay-invoice-select">
                            <SelectValue placeholder="Select invoice (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            {unpaidInvoices.map((inv) => (
                              <SelectItem key={inv.id} value={inv.id}>
                                {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div>
                      <Label>Amount (AED) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        max={wallet?.balance || 0}
                        value={payData.amount}
                        onChange={(e) => setPayData(p => ({ ...p, amount: e.target.value }))}
                        placeholder="Enter amount"
                        data-testid="pay-amount"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={payData.notes}
                        onChange={(e) => setPayData(p => ({ ...p, notes: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={handlePayFromWallet} 
                      disabled={saving || parseFloat(payData.amount) > (wallet?.balance || 0)}
                      data-testid="confirm-pay-btn"
                    >
                      {saving ? 'Processing...' : 'Confirm Payment'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Refund Dialog */}
              <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="wallet-refund-btn">
                    <RefreshCw className="w-4 h-4" /> Refund
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Refund to Wallet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Amount (AED) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={refundData.amount}
                        onChange={(e) => setRefundData(p => ({ ...p, amount: e.target.value }))}
                        placeholder="Enter refund amount"
                        data-testid="refund-amount"
                      />
                    </div>
                    <div>
                      <Label>Reason *</Label>
                      <Input
                        value={refundData.reason}
                        onChange={(e) => setRefundData(p => ({ ...p, reason: e.target.value }))}
                        placeholder="e.g., Service cancellation, Overcharge"
                        data-testid="refund-reason"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={refundData.notes}
                        onChange={(e) => setRefundData(p => ({ ...p, notes: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <Button className="w-full" onClick={handleRefund} disabled={saving} data-testid="confirm-refund-btn">
                      {saving ? 'Processing...' : 'Confirm Refund'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Transaction #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id} data-testid={`wallet-tx-${tx.id}`}>
                    <TableCell className="text-sm">{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="font-mono text-sm">{tx.transaction_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {transactionTypeIcons[tx.transaction_type]}
                        <Badge className={transactionTypeColors[tx.transaction_type] || 'bg-gray-100'}>
                          {tx.transaction_type}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {tx.invoice_number && `Invoice: ${tx.invoice_number}`}
                      {tx.payment_method && `Via ${tx.payment_method}`}
                      {tx.reason && tx.reason}
                      {tx.notes && !tx.invoice_number && !tx.reason && tx.notes}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(tx.balance_after)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
